/** Calendar-date helpers shared by the CSM tabs (UTC, YYYY-MM-DD). */

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The first day of an N-day window ending today (inclusive). */
export function shiftDaysISO(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return d.toISOString().slice(0, 10);
}

/**
 * Drops today's point from a daily series: the day is still running, so its
 * partial total reads as a cliff at the end of the line. Presentation only.
 */
export function withoutPartialToday<T extends { date: string }>(series: readonly T[]): T[] {
  const today = todayISO();
  return series.filter((p) => String(p.date).slice(0, 10) !== today);
}

/** "2026-10-13": the calendar day an instant falls on in Brussels. */
const BRUSSELS_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' });
function brusselsDay(ms: number): number {
  return Date.parse(`${BRUSSELS_DAY.format(ms)}T00:00:00Z`);
}

/**
 * "today", "yesterday", "5d ago", then a plain date beyond a month. Counts
 * calendar days in Brussels time, not 24-hour blocks: a login on 12 Oct at
 * 17:26 is "yesterday" on 13 Oct at 10:00, and 2 Oct is "11d ago" on 13 Oct.
 */
export function relativeDays(
  iso: string | null | undefined,
  fallback: (iso: string) => string,
  now: number = Date.now(),
): string {
  if (!iso) return '';
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return '';
  const days = Math.round((brusselsDay(now) - brusselsDay(at)) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return fallback(iso);
}
